import React, { useState, useEffect } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FiPlus, FiTrash2, FiMove, FiSave, FiLink, FiImage, FiVideo,
  FiShoppingCart, FiType, FiDollarSign, FiLock, FiAward, FiCalendar, FiGrid
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

// Sortable Block Item Component
const SortableBlockItem = ({ block, index, onUpdate, onRemove, renderBlockEditor }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`glass-dark p-4 rounded-xl border border-dark-700 mb-4 ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <button
            {...attributes}
            {...listeners}
            className="text-dark-400 hover:text-lime-400 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <SafeIcon icon={FiMove} />
          </button>
          <span className="text-sm font-medium text-dark-300">
            Block {index + 1}: {block.type}
          </span>
        </div>
        <button
          onClick={() => onRemove(block.id)}
          className="text-red-400 hover:text-red-300 transition-colors"
          title="Delete block"
        >
          <SafeIcon icon={FiTrash2} />
        </button>
      </div>
      {renderBlockEditor(block)}
    </div>
  );
};

const BlocksEditorWithReorder = () => {
  const { blocks, saveBlocks, loading } = useProfile();
  const [localBlocks, setLocalBlocks] = useState(blocks || []);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setLocalBlocks(blocks || []);
  }, [blocks]);

  const blockTypes = [
    { id: 'link', name: 'Link', icon: FiLink, description: 'Add a link to any website' },
    { id: 'image', name: 'Image', icon: FiImage, description: 'Display an image' },
    { id: 'video', name: 'Video', icon: FiVideo, description: 'Embed YouTube or other videos' },
    { id: 'product', name: 'Product', icon: FiShoppingCart, description: 'Showcase a product for sale' },
    { id: 'calendar', name: 'Calendar', icon: FiCalendar, description: 'Add calendar/scheduling link' },
    { id: 'gallery', name: 'Image Gallery', icon: FiGrid, description: 'Display multiple images' },
    { id: 'text', name: 'Text', icon: FiType, description: 'Add formatted text content' },
    { id: 'tipjar', name: 'Tip Jar', icon: FiDollarSign, description: 'Add tip jar for supporters' },
    { id: 'nftgallery', name: 'NFT Gallery', icon: FiAward, description: 'Display NFT collection' },
    { id: 'tokengated', name: 'Token Gated', icon: FiLock, description: 'Content for NFT holders' },
  ];

  const addBlock = (type) => {
    const newBlock = {
      id: uuidv4(),
      type,
      data: getDefaultBlockData(type),
      order_index: localBlocks.length,
    };
    setLocalBlocks([...localBlocks, newBlock]);
  };

  const getDefaultBlockData = (type) => {
    switch (type) {
      case 'link':
        return { title: 'New Link', url: '', description: '', icon: '🔗' };
      case 'image':
        return { title: '', url: '', description: '' };
      case 'video':
        return { title: '', url: '', description: '' };
      case 'product':
        return { title: 'New Product', description: '', price: '$0', url: '', image: '', cta: 'Buy Now' };
      case 'calendar':
        return { title: 'Book a Meeting', url: '', description: 'Schedule a call with me', provider: 'cal.com' };
      case 'gallery':
        return { title: 'Image Gallery', images: [], layout: 'grid' };
      case 'text':
        return { title: 'New Text Block', content: 'Add your content here...' };
      case 'tipjar':
        return { enabled: true, currencies: ['ETH', 'USDC', 'USD'], presetAmounts: { ETH: [0.01, 0.05, 0.1, 0.5, 1], USDC: [5, 10, 25, 50, 100], USD: [5, 10, 25, 50, 100] } };
      case 'nftgallery':
        return { enabled: true, contractAddress: '', minBalance: 1, network: 'ethereum' };
      case 'tokengated':
        return { contentType: 'text', title: 'Exclusive Content', content: 'This content is only available to NFT holders', requirements: { contractAddress: '', minBalance: 1, network: 'ethereum' } };
      default:
        return {};
    }
  };

  const updateBlock = (blockId, newData) => {
    setLocalBlocks(
      localBlocks.map(block =>
        block.id === blockId ? { ...block, data: { ...block.data, ...newData } } : block
      )
    );
  };

  const removeBlock = (blockId) => {
    setLocalBlocks(localBlocks.filter(block => block.id !== blockId));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalBlocks((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        
        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update order_index for all blocks
        return newItems.map((item, index) => ({
          ...item,
          order_index: index,
        }));
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveBlocks(localBlocks);
      if (result.success) {
        toast.success('Blocks saved successfully!');
      } else {
        toast.error(`Error saving blocks: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderBlockEditor = (block) => {
    switch (block.type) {
      case 'link':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={block.data.title || ''}
                onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                placeholder="Link Title"
                className="px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
              />
              <input
                type="text"
                value={block.data.icon || ''}
                onChange={(e) => updateBlock(block.id, { icon: e.target.value })}
                placeholder="Icon (emoji)"
                className="px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
              />
            </div>
            <input
              type="url"
              value={block.data.url || ''}
              onChange={(e) => updateBlock(block.id, { url: e.target.value })}
              placeholder="https://example.com"
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
            />
            <textarea
              value={block.data.description || ''}
              onChange={(e) => updateBlock(block.id, { description: e.target.value })}
              placeholder="Description (optional)"
              rows={2}
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700 resize-none"
            />
          </div>
        );
      case 'gallery':
        return (
          <div className="space-y-4">
            <input
              type="text"
              value={block.data.title || ''}
              onChange={(e) => updateBlock(block.id, { title: e.target.value })}
              placeholder="Gallery Title"
              className="w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
            />
            <p className="text-xs text-dark-400">
              Image gallery block - full implementation coming soon
            </p>
          </div>
        );
      default:
        return (
          <div className="text-sm text-dark-400">
            Block editor for {block.type} - coming soon
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Block Buttons */}
      <div className="glass-dark p-6 rounded-xl border border-dark-700">
        <h3 className="text-lg font-semibold text-lime-bright mb-4">Add Block</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {blockTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => addBlock(type.id)}
              className="glass p-4 rounded-lg border border-dark-700 hover:border-lime-500/50 transition-colors text-center"
            >
              <SafeIcon icon={type.icon} className="text-2xl text-lime-400 mx-auto mb-2" />
              <p className="text-xs text-dark-200 font-medium">{type.name}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Blocks List with Drag & Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={localBlocks.map(b => b.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {localBlocks.length === 0 ? (
              <div className="glass-dark p-12 rounded-xl border border-dark-700 text-center">
                <SafeIcon icon={FiGrid} className="text-4xl text-dark-500 mx-auto mb-4" />
                <p className="text-dark-400">No blocks yet. Add one above!</p>
              </div>
            ) : (
              localBlocks.map((block, index) => (
                <SortableBlockItem
                  key={block.id}
                  block={block}
                  index={index}
                  onUpdate={updateBlock}
                  onRemove={removeBlock}
                  renderBlockEditor={renderBlockEditor}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>

      {/* Save Button */}
      {localBlocks.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="glass-lime text-lime-400 px-6 py-3 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 hover:border-lime-400/70 shadow-glow-lime-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
          >
            <SafeIcon icon={FiSave} className="mr-2" />
            {saving ? 'Saving...' : 'Save Blocks'}
          </button>
        </div>
      )}
    </div>
  );
};

export default BlocksEditorWithReorder;

